import { getChatCompletion } from './aiIntegrations/chatCompletion';

const SYSTEM_PROMPT = `You are an expert data strategy consultant AI assistant for Northstar Analytics. Your role is to provide helpful, context-aware guidance about data strategy, frameworks, and consultation services.

**About Northstar Analytics:**
Northstar Analytics is a specialist data strategy consultancy helping organisations unlock the value of their data. Founded by Mark, a seasoned Chief Data Officer, Northstar delivers practical, outcome-focused data strategy, governance, and AI readiness services.

**Your Expertise:**
- Data maturity assessments and improvement strategies
- COMPASS Framework (data strategy foundation — Culture, Operations, Measurement, People, Architecture, Security, Strategy)
- ELEVATE Framework (analytics advancement)
- NAVIGATE Framework (governance and compliance)
- AI readiness and implementation
- Fractional CDO services
- ROI analysis for data initiatives
- Data Governance Diagnostics

**Key Services & Assessments:**
- Free Data Maturity Assessment (/data-maturity-calculator-lead-generation-assessment)
- Free Data Governance Diagnostic (/data-governance-diagnostic-assessment)
- Free AI Readiness Assessment (/free-assessments-hub)
- Strategic Consultation Bookings (/schedule-consultation)
- AI Solutions & Strategy (/ai-solutions-intelligent-data-strategy-services)
- Fractional CDO Services (/fractional-cdo-services-executive-leadership-solutions)
- ROI Calculator Suite (/roi-calculator-suite-value-demonstration-tools)
- Knowledge Hub articles and thought leadership (/knowledge-hub-thought-leadership-center)
- Services Overview (/services-overview-interactive-capability-showcase)
- Contact & Custom Proposals (/contact-multi-channel-engagement-hub)

**Your Personality:**
- Professional yet approachable
- Clear and concise in explanations
- Proactive in suggesting next steps
- Focused on business value and outcomes

**Conversation Guidelines:**
1. Ask clarifying questions to understand user needs
2. Provide specific, actionable advice
3. Reference relevant frameworks when appropriate
4. Guide users toward booking consultations for complex needs
5. Keep responses concise (2-3 paragraphs max)
6. Use bullet points for clarity when listing options
7. When relevant, mention specific free assessments or tools users can try immediately

**When to Recommend Consultation:**
- User has complex, organisation-specific challenges
- Multiple frameworks or services are needed
- User expresses readiness to take action
- Technical implementation questions arise

Always maintain a helpful, consultative tone focused on solving the user's data strategy challenges.`;

export const chatService = {
  /**
   * Send a message and get AI response with conversation history
   */
  async sendMessage(userMessage, conversationHistory = []) {
    const startTime = Date.now();

    try {
      const messages = [
        { role: 'system', content: SYSTEM_PROMPT },
        ...conversationHistory,
        { role: 'user', content: userMessage }
      ];

      const result = await getChatCompletion(
        'OPEN_AI',
        'gpt-5',
        messages
      );

      // Detect Lambda error response (returns {error, details} instead of {choices})
      if (result?.error) {
        console.error('Chat completion error from Lambda:', result?.error, result?.details);
        return {
          success: false,
          error: result?.details || result?.error || 'AI service returned an error.',
          conversationHistory
        };
      }

      const assistantMessage = result?.choices?.[0]?.message?.content;

      // Guard against empty/undefined content
      if (!assistantMessage) {
        console.error('Chat completion returned empty content:', result);
        return {
          success: false,
          error: 'The AI returned an empty response. Please try again.',
          conversationHistory
        };
      }

      const responseTime = Date.now() - startTime;

      return {
        success: true,
        message: assistantMessage,
        conversationHistory: [
          ...conversationHistory,
          { role: 'user', content: userMessage },
          { role: 'assistant', content: assistantMessage }
        ],
        responseTime
      };
    } catch (error) {
      console.error('Chat service error:', error);
      return {
        success: false,
        error: error?.message || 'An unexpected error occurred. Please try again.',
        conversationHistory
      };
    }
  },

  /**
   * Get suggested quick replies based on conversation context
   */
  getSuggestedReplies(conversationHistory = []) {
    if (conversationHistory?.length === 0) {
      return [
        "What is data maturity?",
        "Tell me about your frameworks",
        "How can AI help my business?",
        "I need help with data strategy"
      ];
    }

    const lastMessage = conversationHistory?.[conversationHistory?.length - 1];
    if (lastMessage?.role === 'assistant') {
      const content = lastMessage?.content?.toLowerCase();

      if (content?.includes('consultation') || content?.includes('book')) {
        return [
          "How do I book a consultation?",
          "What should I prepare?",
          "Tell me more about your services"
        ];
      }

      if (content?.includes('framework') || content?.includes('compass') || content?.includes('elevate')) {
        return [
          "How do I get started?",
          "What's the implementation timeline?",
          "Can you assess my organisation?"
        ];
      }

      if (content?.includes('ai') || content?.includes('artificial intelligence')) {
        return [
          "What's an AI readiness assessment?",
          "How much does it cost?",
          "Show me case studies"
        ];
      }

      if (content?.includes('governance') || content?.includes('diagnostic')) {
        return [
          "Take the free diagnostic",
          "What does governance involve?",
          "Book a consultation"
        ];
      }
    }

    return [
      "Tell me more",
      "What are the next steps?",
      "How do I get started?"
    ];
  },

  /**
   * Generate context-aware greeting based on page/user state
   */
  getContextualGreeting(context = {}) {
    const { page, userName, hasBooking } = context;
    const greeting = userName ? `Hi ${userName}! 👋` : 'Hello! 👋';

    if (hasBooking) {
      return `${greeting} I see you have an upcoming consultation. How can I help you prepare?`;
    }

    switch (page) {
      case 'home':
        return `${greeting} I'm your Northstar data strategy assistant. I can help you understand our frameworks, assess your data maturity, or guide you toward the right services. What would you like to explore?`;

      case 'services':
        return `${greeting} I can help you understand our services and find the right solution for your organisation. What specific challenges are you facing?`;

      case 'ai-solutions':
        return `${greeting} Interested in AI? I can explain our AI readiness assessments, implementation strategies, and how to ensure your data foundation is ready. What would you like to know?`;

      case 'post-booking':
        return `${greeting} Congratulations on booking your consultation! I'm here to help you prepare. Would you like tips on what to bring or questions to consider?`;

      case 'knowledge-hub':
        return `${greeting} Welcome to the Knowledge Hub! I can help you find relevant articles, explain our frameworks, or answer questions about data strategy. What are you looking for?`;

      case 'assessments':
        return `${greeting} Our free assessments are a great starting point. I can walk you through the Data Maturity Assessment, Data Governance Diagnostic, or AI Readiness Assessment. Which interests you most?`;

      case 'roi':
        return `${greeting} Our ROI calculators help quantify the value of data investments. I can help you understand the results or discuss how to build a business case. What would you like to explore?`;

      default:
        return `${greeting} I'm here to help with any questions about data strategy, our frameworks, or Northstar Analytics services. What can I assist you with today?`;
    }
  },
};

export default chatService;